/**
 * ConceptEditor — la tabla de conceptos de una cotización o una factura,
 * editable, con vista previa.
 *
 * Vive acá y no duplicada en cada detalle (REGLA #13): la cotización y la
 * factura imprimen la MISMA tabla, y hasta ahora cada una tenía su copia del
 * editor. Agregar el tipo "nota" en una sola de las dos habría dejado a la
 * factura sin poder mostrar lo que la cotización ya decía.
 *
 * Una línea es una de dos cosas:
 *  · `item` — descripción, cantidad, precio, importe.
 *  · `note` — texto libre a todo el ancho, con saltos de línea, sin números y
 *    sin sumar al subtotal.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Icon } from '../Icon.js';
import { formatMoney } from '../../lib/money.js';
import { Dropdown } from '../ui/Dropdown.js';
import { useBillingItems, type EstimateLineItem, type LineItemKind } from '../../hooks/useBilling.js';

export interface LineItemForm {
  kind: LineItemKind;
  description: string;
  qty: string;
  unitPriceDollars: string;
}

export const EMPTY_LINE: LineItemForm = { kind: 'item', description: '', qty: '1', unitPriceDollars: '' };
export const EMPTY_NOTE: LineItemForm = { kind: 'note', description: '', qty: '0', unitPriceDollars: '0' };

/** De lo guardado al formulario. `kind` ausente = documento viejo, era un item. */
export function linesFromItems(items: EstimateLineItem[]): LineItemForm[] {
  if (items.length === 0) return [{ ...EMPTY_LINE }];
  return items.map(li => ({
    kind: li.kind ?? 'item',
    description: li.description,
    qty: String(li.qty),
    unitPriceDollars: (li.unitPrice / 100).toFixed(2),
  }));
}

/** Del formulario a lo que se manda. Una nota va sin números y con sus saltos. */
export function itemsFromLines(lines: LineItemForm[]): Array<{ kind: LineItemKind; description: string; qty: number; unitPrice: number }> {
  return lines
    .filter(l => l.description.trim())
    .map(l => ({
      kind: l.kind,
      description: l.kind === 'note' ? l.description.replace(/^\s+|\s+$/g, '') : l.description.trim(),
      qty: l.kind === 'note' ? 0 : Math.max(0, Math.trunc(Number(l.qty) || 0)),
      unitPrice: l.kind === 'note' ? 0 : Math.max(0, Math.round((Number(l.unitPriceDollars) || 0) * 100)),
    }));
}

/** Lo que se ve en la vista previa: lo del formulario, no lo guardado. */
export function previewItems(lines: LineItemForm[]): EstimateLineItem[] {
  const qtyOf = (l: LineItemForm) => Math.max(0, Math.trunc(Number(l.qty) || 0));
  const priceOf = (l: LineItemForm) => Math.max(0, Math.round((Number(l.unitPriceDollars) || 0) * 100));
  return lines.map(l => ({
    kind: l.kind,
    description: l.description,
    qty: l.kind === 'note' ? 0 : qtyOf(l),
    unitPrice: l.kind === 'note' ? 0 : priceOf(l),
    amount: l.kind === 'note' ? 0 : qtyOf(l) * priceOf(l),
  }));
}

/** Botón que alterna editar ↔ ver cómo queda. Va en la cabecera de la tarjeta. */
export function ConceptPreviewToggle({ preview, onToggle }: { preview: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="link" tone="cyan" size="sm" onClick={onToggle}>
      {t(preview ? 'billing.estimates.editToggle' : 'billing.estimates.previewToggle')}
    </Button>
  );
}

interface Props {
  lines: LineItemForm[];
  onChange: (lines: LineItemForm[]) => void;
  currency: string;
  /** Rejilla de la tabla en modo edición (incluye la columna de quitar). */
  cols: string;
}

export function ConceptEditor({ lines, onChange, currency, cols }: Props) {
  const { t } = useTranslation();
  const { data: catalogo } = useBillingItems();
  const items = catalogo?.items ?? [];

  function update(idx: number, patch: Partial<LineItemForm>) {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function remove(idx: number) {
    onChange(lines.length > 1 ? lines.filter((_, i) => i !== idx) : lines);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', paddingTop: 'var(--evo-space-2)' }}>
      {lines.map((line, idx) => (
        line.kind === 'note' ? (
          // La nota ocupa el ancho de la tabla: dos columnas de números vacías
          // al lado de un párrafo se leen como un error del documento.
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 32px', gap: 'var(--evo-space-2)', alignItems: 'start' }}>
            <Textarea
              value={line.description}
              onChange={v => update(idx, { description: v })}
              rows={2}
              placeholder={t('billing.estimates.notePlaceholder')}
              fullWidth
            />
            <IconButton
              icon={<Icon name="close" size={14} />}
              size="sm"
              tone="muted"
              onClick={() => remove(idx)}
              ariaLabel={t('billing.estimates.removeLine')}
            />
          </div>
        ) : (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: cols, gap: 'var(--evo-space-2)', alignItems: 'center' }}>
            <Input value={line.description} onChange={v => update(idx, { description: v })} size="sm" fullWidth />
            <Input type="number" value={line.qty} onChange={v => update(idx, { qty: v })} size="sm" fullWidth />
            <Input type="number" value={line.unitPriceDollars} onChange={v => update(idx, { unitPriceDollars: v })} size="sm" fullWidth />
            <span className="text-sm" style={{ color: 'var(--evo-fg-2)', textAlign: 'right' }}>
              {formatMoney(
                Math.max(0, Math.trunc(Number(line.qty) || 0)) * Math.max(0, Math.round((Number(line.unitPriceDollars) || 0) * 100)),
                currency,
              )}
            </span>
            <IconButton
              icon={<Icon name="close" size={14} />}
              size="sm"
              tone="muted"
              onClick={() => remove(idx)}
              ariaLabel={t('billing.estimates.removeLine')}
            />
          </div>
        )
      ))}
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="outline" tone="muted" size="sm" onClick={() => onChange([...lines, { ...EMPTY_LINE }])}>
          {t('billing.estimates.addLine')}
        </Button>
        <Button variant="outline" tone="muted" size="sm" onClick={() => onChange([...lines, { ...EMPTY_NOTE }])}>
          {t('billing.estimates.addNote')}
        </Button>
        {/* Traer un concepto guardado COPIA descripción y precio a una línea
            nueva, con cantidad 1. No queda atado al catálogo: la línea se
            edita como cualquier otra y cambiar el precio guardado mañana no
            reescribe este documento. */}
        <Dropdown<number>
          value={null}
          placeholder={items.length === 0 ? t('billing.estimates.itemPickNone') : t('billing.estimates.itemPick')}
          options={items.map(item => ({
            value: item.id,
            label: `${item.description} · ${formatMoney(item.unitPrice, item.currency)}`,
          }))}
          onChange={id => {
            const item = items.find(i => i.id === id);
            if (!item) return;
            onChange([...lines, {
              kind: 'item',
              description: item.description,
              qty: '1',
              unitPriceDollars: (item.unitPrice / 100).toFixed(2),
            }]);
          }}
          disabled={items.length === 0}
          size="sm"
          searchable
        />
      </div>
    </div>
  );
}

/** Estado del alternador. Local acá para que ninguna página lo reimplemente. */
export function useConceptPreview() {
  const [preview, setPreview] = useState(false);
  return { preview, toggle: () => setPreview(v => !v) };
}
